import * as React from 'react';
import {FunctionComponent} from 'react';
// @ts-ignore
import GoogleMapReact from 'google-map-react';
import {Duration} from '../../../../Domain/Duration';
import {GeoPoint} from '../../../../Models/GeoPoint';
import {Speed} from '../../../../Domain/Speed';
import {Distance} from '../../../../Domain/Distance';
import {centerPointOfGeoTrack} from '../../../../Geo/centerPointOfGeoTrack';
import {distanceBetweenGeoPoints} from '../../../../Geo/distanceBetweenGeoPoints';

export interface MapSegment {
    from: GeoPoint;
    to: GeoPoint;
    speed: Speed;
    isRest: boolean;
    elapsedTime: Duration;
}

function getColor(minSpeed: Speed, maxSpeed: Speed, section: MapSegment): {r: number; g: number; b: number} {
    const colorRamp: Array<{r: number; g: number; b: number}> = [
        {r: 27, g: 104, b: 114},
        {r: 58, g: 196, b: 231},
    ];
    const restColor: {r: number; g: number; b: number} = {r: 178, g: 178, b: 178};
    const colorRoundTreshold: number = 0.2;

    if (section.isRest) {
        return restColor;
    } else {
        // Calculate normalized speed.
        let normalizedSpeed: number =
            (section.speed.metersPerSecond - minSpeed.metersPerSecond) /
            (maxSpeed.metersPerSecond - minSpeed.metersPerSecond);
        if (normalizedSpeed < colorRoundTreshold || normalizedSpeed > 1 - colorRoundTreshold) {
            normalizedSpeed = Math.round(normalizedSpeed);
        } else {
            normalizedSpeed = (normalizedSpeed - colorRoundTreshold) / (1 - 2 * colorRoundTreshold);
        }

        // Set color using colorramp.
        return {
            r: Math.round(colorRamp[1].r - (colorRamp[1].r - colorRamp[0].r) * normalizedSpeed),
            g: Math.round(colorRamp[1].g - (colorRamp[1].g - colorRamp[0].g) * normalizedSpeed),
            b: Math.round(colorRamp[1].b - (colorRamp[1].b - colorRamp[0].b) * normalizedSpeed),
        };
    }
}

interface OwnProps {
    height: number;
    sections: MapSegment[];
    selectedGeoPoint?: GeoPoint;
    onGeoPointClicked: (geoPoint: GeoPoint) => void;
}

const Marker: any = () => (
    <div
        style={{
            padding: '8px',
            borderRadius: '50%',
            backgroundColor: 'var(--bs-primary)',
            border: 'solid var(--bs-black) 1px',
            transform: 'translate(-50%, -50%)',
        }}
    />
);
export const GeoTrackMapColored: FunctionComponent<OwnProps> = ({
    height,
    sections,
    selectedGeoPoint,
    onGeoPointClicked,
}) => {
    const defaultProps = {
        center: centerPointOfGeoTrack(sections.map((s) => s.from)),
        zoom: 13,
    };

    function handleApiLoaded(map: any, maps: any, mapSections: MapSegment[]) {
        const minSpeed: Speed = Speed.fromMetersPerSecond(
            Math.min.apply(
                0,
                mapSections.map((s) => s.speed.metersPerSecond),
            ),
        );
        const maxSpeed: Speed = Speed.fromMetersPerSecond(
            Math.max.apply(
                0,
                mapSections.map((s) => s.speed.metersPerSecond),
            ),
        );
        for (const entry of mapSections) {
            const color: {r: number; g: number; b: number} = getColor(minSpeed, maxSpeed, entry);

            // Draw the line on the map.
            new maps.Polyline({
                path: [entry.from, entry.to],
                geodesic: true,
                strokeColor: 'rgb(' + color.r + ',' + color.g + ',' + color.b + ')',
                strokeOpacity: 1,
                strokeWeight: 4,
            }).setMap(map);
        }

        // Change map bounds based on geoTrack.
        const bounds = new maps.LatLngBounds();
        mapSections.forEach((section) => {
            bounds.extend(new maps.LatLng(section.from.lat, section.from.lng));
        });
        map.fitBounds(bounds);
    }

    function setNearestGeoPoint(event: any) {
        const clickedGeoPoint: GeoPoint = {lat: event.lat, lng: event.lng, elapsed_time: Duration.fromSeconds(0)};
        const nearestSection: MapSegment = [...sections].sort((a, b) =>
            distanceBetweenGeoPoints(clickedGeoPoint, a.from).millimeters <
            distanceBetweenGeoPoints(clickedGeoPoint, b.from).millimeters
                ? -1
                : 1,
        )[0];
        const nearestDistance: Distance = distanceBetweenGeoPoints(clickedGeoPoint, nearestSection.from);
        if (nearestDistance.meters < 50) {
            // For now use a lat-lng distance to determine clicking near due to not having access to the screen-pixel location of the tracks.
            onGeoPointClicked(nearestSection.from);
        }
    }

    const mapOptions = {
        disableDefaultUI: true,
        styles: [
            {
                featureType: 'water',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#a0d6d1',
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
            {
                featureType: 'landscape',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#ffffff',
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.fill',
                stylers: [
                    {
                        color: '#dedede',
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.stroke',
                stylers: [
                    {
                        color: '#dedede',
                    },
                    {
                        lightness: 29,
                    },
                    {
                        weight: 0.2,
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#dedede',
                    },
                    {
                        lightness: 18,
                    },
                ],
            },
            {
                featureType: 'road.local',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#ffffff',
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                featureType: 'poi',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#f1f1f1',
                    },
                    {
                        lightness: 21,
                    },
                ],
            },
            {
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#ffffff',
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                elementType: 'labels.text.fill',
                stylers: [
                    {
                        saturation: 36,
                    },
                    {
                        color: '#333333',
                    },
                    {
                        lightness: 40,
                    },
                ],
            },
            {
                elementType: 'labels.icon',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'transit',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#f2f2f2',
                    },
                    {
                        lightness: 19,
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'geometry.fill',
                stylers: [
                    {
                        color: '#fefefe',
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'geometry.stroke',
                stylers: [
                    {
                        color: '#fefefe',
                    },
                    {
                        lightness: 17,
                    },
                    {
                        weight: 1.2,
                    },
                ],
            },
        ],
    };
    return (
        <div style={{height: height, width: '100%'}}>
            <GoogleMapReact
                bootstrapURLKeys={{key: 'AIzaSyARpp-uOe0QHWfqqKt0Xvv4Q9DaNCXarU0'}}
                defaultCenter={defaultProps.center}
                defaultZoom={defaultProps.zoom}
                options={mapOptions}
                onClick={(event: any) => setNearestGeoPoint(event)}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({map, maps}: any) => handleApiLoaded(map, maps, sections)}
            >
                {selectedGeoPoint && (
                    <Marker lat={selectedGeoPoint.lat} lng={selectedGeoPoint.lng} zoom={defaultProps.zoom} />
                )}
            </GoogleMapReact>
        </div>
    );
};
